import { useState } from 'react';
import SectionHeading from '@/components/base/SectionHeading';
import Reveal from '@/components/base/Reveal';
import { fileList } from '@/mocks/evidence';

const statusStyle: Record<string, string> = {
  已核验: 'bg-accent-100 text-accent-900',
  已交付: 'bg-accent-100 text-accent-900',
  已归档: 'bg-secondary-100 text-secondary-900',
  已发布: 'bg-secondary-100 text-secondary-900',
  申报中: 'bg-primary-100 text-primary-800',
};

const categories = ['全部', '资质', '项目', '技术', '合规'] as const;
type Category = (typeof categories)[number];

export default function FileDownload() {
  const [downloadedId, setDownloadedId] = useState<string | null>(null);
  const [filter, setFilter] = useState<Category>('全部');

  const filtered = filter === '全部' ? fileList : fileList.filter((f) => f.category === filter);

  const handleDownload = (file: (typeof fileList)[number]) => {
    const content = [
      `文件编号：${file.id}`,
      `文件名称：${file.name}`,
      `文件类型：${file.type}`,
      `核验状态：${file.status}`,
      `更新时间：${file.date}`,
      '',
      `文件说明：${file.desc}`,
      '',
      '注：本文件为华辉数智技术 / 九畴云市项目文件摘要，正式资质文件与完整项目文档可联系 huahuitechnology@hhsztech.cn 获取。',
    ].join('\n');

    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${file.id}-${file.name}.txt`;
    a.click();
    URL.revokeObjectURL(url);

    setDownloadedId(file.id);
    window.setTimeout(() => setDownloadedId(null), 3000);
  };

  return (
    <section className="w-full bg-background-100 py-16 md:py-24">
      <div className="max-w-6xl mx-auto px-4 md:px-6">
        <Reveal>
          <SectionHeading
            eyebrow="文件清单"
            title="资质与项目文件清单"
            subtitle="企业资质、项目文件、技术成果与合规文件集中归档，点击下载可获取文件说明摘要。"
          />
        </Reveal>

        <Reveal delay={100}>
          {/* 类型筛选 */}
          <div className="mt-8 flex flex-wrap justify-center gap-2">
            {categories.map((c) => (
              <button
                key={c}
                onClick={() => setFilter(c)}
                className={`px-4 py-2 text-sm rounded-full whitespace-nowrap cursor-pointer transition-colors ${
                  filter === c
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-50 text-foreground-600 border border-background-200 hover:bg-background-200'
                }`}
              >
                {c}
              </button>
            ))}
          </div>

          {/* 核验说明 */}
          <div className="mt-6 flex items-start gap-3 rounded-lg border border-secondary-200 bg-secondary-50 p-4">
            <i className="ri-shield-check-line text-secondary-600 text-lg mt-0.5"></i>
            <p className="text-sm text-foreground-700 leading-relaxed">
              <strong className="text-foreground-900 font-semibold">核验说明：</strong>
              上述资质与项目文件均来自华辉数智技术及九畴云市正式归档材料，编号与状态可追溯；正式资质文件与完整项目文档可联系 huahuitechnology@hhsztech.cn 线下核验。
            </p>
          </div>

          <div className="mt-6 rounded-lg border border-background-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-background-100 border-b border-background-200 text-left">
                    <th className="px-4 py-3 font-medium text-foreground-500 whitespace-nowrap">编号</th>
                    <th className="px-4 py-3 font-medium text-foreground-500 whitespace-nowrap">文件名称</th>
                    <th className="px-4 py-3 font-medium text-foreground-500 whitespace-nowrap">类型</th>
                    <th className="px-4 py-3 font-medium text-foreground-500 whitespace-nowrap">状态</th>
                    <th className="px-4 py-3 font-medium text-foreground-500 whitespace-nowrap">更新时间</th>
                    <th className="px-4 py-3 font-medium text-foreground-500 whitespace-nowrap">下载</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((file) => (
                    <tr
                      key={file.id}
                      className="border-b border-background-100 last:border-0 hover:bg-background-100/60 transition-colors"
                    >
                      <td className="px-4 py-3 text-foreground-500 font-mono whitespace-nowrap">{file.id}</td>
                      <td className="px-4 py-3">
                        <div className="font-medium text-foreground-900">{file.name}</div>
                        <div className="text-xs text-foreground-500 mt-0.5 leading-snug max-w-xs">{file.desc}</div>
                      </td>
                      <td className="px-4 py-3 text-foreground-600 whitespace-nowrap">{file.type}</td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span
                          className={`px-2.5 py-1 text-[11px] rounded-full whitespace-nowrap ${
                            statusStyle[file.status] || 'bg-background-100 text-foreground-600'
                          }`}
                        >
                          {file.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-foreground-500 whitespace-nowrap">{file.date}</td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => handleDownload(file)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-primary-400 text-primary-600 text-xs font-medium whitespace-nowrap cursor-pointer transition-colors hover:bg-primary-50"
                          aria-label={`下载 ${file.name}`}
                        >
                          <i className="ri-download-2-line text-sm"></i>
                          {downloadedId === file.id ? '已下载' : '下载'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {downloadedId && (
            <p className="mt-4 text-sm text-accent-600">
              文件摘要已下载，正式资质文件与完整项目文档可联系 huahuitechnology@hhsztech.cn 获取。
            </p>
          )}
        </Reveal>
      </div>
    </section>
  );
}